import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTranslation } from '@/contexts/LanguageContext';
import { useDateLocale } from '@/hooks/useDateLocale';
import { format, getDaysInMonth } from 'date-fns';

interface MonthYearPickerProps {
  selectedMonth: number | null;
  selectedYear: number | null;
  onMonthChange: (month: number) => void;
  onYearChange: (year: number) => void;
}

export function MonthYearPicker({
  selectedMonth,
  selectedYear,
  onMonthChange,
  onYearChange,
}: MonthYearPickerProps) {
  const { t } = useTranslation();
  const locale = useDateLocale();
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  const getMonthName = (month: number) => {
    return format(new Date(2000, month - 1, 1), 'MMMM', { locale });
  };

  const getLastDayOfMonth = () => {
    if (selectedMonth && selectedYear) {
      return getDaysInMonth(new Date(selectedYear, selectedMonth - 1));
    }
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('month')}</Label>
          <Select
            value={selectedMonth?.toString() || ''}
            onValueChange={(value) => onMonthChange(parseInt(value))}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('selectMonth')} />
            </SelectTrigger>
            <SelectContent className="z-[100]">
              {months.map((month) => (
                <SelectItem key={month} value={month.toString()}>
                  {getMonthName(month)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>{t('year')}</Label>
          <Select
            value={selectedYear?.toString() || ''}
            onValueChange={(value) => onYearChange(parseInt(value))}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('selectYear')} />
            </SelectTrigger>
            <SelectContent className="z-[100]">
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {selectedMonth && selectedYear && (
        <p className="text-sm text-muted-foreground">
          {t('validityPeriod')}: 01/{selectedMonth.toString().padStart(2, '0')}/{selectedYear} -{' '}
          {getLastDayOfMonth()}/{selectedMonth.toString().padStart(2, '0')}/{selectedYear}
        </p>
      )}
    </div>
  );
}
